import React, { useState, useMemo, useEffect } from "react";
import { Table, Button, Icon, Label, Popup, Dropdown } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { openModal } from "../../../app/common/modals/modalSlice";
import { updateComplianceItemStatusInDb, fetchComplianceItemsFromDb } from "../../../app/firestore/firestoreService";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { onSnapshot } from "firebase/firestore";
import { getConditionalRequirementInfo } from "../complianceConditionUtils";

export default function ComplianceChecklistTable({ template, transaction, documents, showConditional = false, showOptional = false, brokerageForms = [] }) {
  const dispatch = useDispatch();
  const [processingItems, setProcessingItems] = useState({});
  const [savedComplianceItems, setSavedComplianceItems] = useState({});

  // Listen for saved compliance items from database
  useEffect(() => {
    if (!transaction?.id) return;

    const unsubscribe = onSnapshot(
      fetchComplianceItemsFromDb(transaction.id),
      (snapshot) => {
        const items = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          // Use the document ID as the key
          items[doc.id] = data;
          console.log("Loaded compliance item:", doc.id, data);
        });
        console.log("All savedComplianceItems:", items);
        setSavedComplianceItems(items);
      },
      (error) => {
        console.error("Error fetching saved compliance items:", error);
      }
    );

    return () => unsubscribe();
  }, [transaction, transaction.id]);

  // Build a map of brokerage forms that replace parent forms
  // Key: parent form title (documentName), Value: brokerage form
  const brokerageReplacementMap = useMemo(() => {
    const map = {};
    if (!brokerageForms || brokerageForms.length === 0) return map;

    brokerageForms.forEach((form) => {
      if (form.brokerageReplacesParentFormTitle) {
        map[form.brokerageReplacesParentFormTitle] = form;
      }
    });
    return map;
  }, [brokerageForms]);

  // Build compliance items from template
  const complianceItems = useMemo(() => {
    if (!template?.categories) return [];

    const items = [];
    const propertyType = transaction?.propertyType || "Residential";

    template.categories.forEach((category) => {
      if (!category.items) return;

      category.items.forEach((item) => {
        // Check if item applies to this transaction's property type
        const appliesToPropertyType =
          !item.appliesToPropertyTypes ||
          item.appliesToPropertyTypes.length === 0 ||
          item.appliesToPropertyTypes.includes(propertyType) ||
          item.appliesToPropertyTypes.includes("All");

        if (appliesToPropertyType) {
          // Check if there's a brokerage form that replaces this item
          const replacementForm = brokerageReplacementMap[item.documentName];

          // Find all matching documents by documentName
          // The template maps documentName to documentType and documentTypeSubtype
          let matchingDocs = documents.filter((doc) => {
            if (!doc.signingComplete) return false;

            // Match by documentName only
            if (item.documentName && doc.documentName === item.documentName) {
              return true;
            }

            return false;
          });

          // If there's a replacement brokerage form, also look for its documents
          if (replacementForm) {
            const brokerageFormDocs = documents.filter((doc) => {
              if (!doc.signingComplete) return false;
              // Match by brokerage form title or formId
              return doc.isBrokerageForm && (doc.title === replacementForm.title || doc.formId === replacementForm.id);
            });
            // Combine with any existing matching docs
            matchingDocs = [...matchingDocs, ...brokerageFormDocs];
          }

          // Create item key to match with saved items
          const itemKey = `${category.name}-${item.name}`;
          const savedItem = savedComplianceItems[itemKey];

          if (savedItem) {
            console.log("Found savedItem for key:", itemKey, "savedItem:", savedItem);
          }

          items.push({
            ...item,
            templateRequirement: item.requirement,
            category: category.name,
            categoryOrder: category.order,
            documents: matchingDocs,
            status: matchingDocs.length > 0 ? "complete" : (item.status || "pending"),
            // Include replacement form info if applicable
            replacementForm: replacementForm || null,
            // Override with saved data if it exists
            ...(savedItem && {
              status: savedItem.status,
              requirement: savedItem.requirement || item.requirement,
            }),
          });
        }
      });
    });

    const itemsWithConditionalRequirements = items.map((item) => {
      const conditionalRequirementInfo = getConditionalRequirementInfo(item, items, transaction);
      if (!conditionalRequirementInfo) return item;

      const updatedItem = { ...item, ...conditionalRequirementInfo };

      if (conditionalRequirementInfo.effectiveRequirement === "required" && item.status === "not-needed") {
        return {
          ...updatedItem,
          status: item.documents && item.documents.length > 0 ? "complete" : "pending",
        };
      }

      return updatedItem;
    });

    // Filter out optional and conditional items based on checkbox settings
    const filteredItems = itemsWithConditionalRequirements.filter((item) => {
      const requirement = item.effectiveRequirement || item.requirement;

      // Always show required items
      if (requirement === "required") return true;

      // Always show items marked as "not-needed"
      if (item.status === "not-needed") return true;

      // For conditional items: show if checkbox is checked OR if they have documents
      if (requirement === "conditional") {
        return showConditional || (item.documents && item.documents.length > 0);
      }

      // For optional items: show if checkbox is checked OR if they have documents
      if (requirement === "optional") {
        return showOptional || (item.documents && item.documents.length > 0);
      }

      // Show all other items by default
      return true;
    });

    // Sort by category order, then item order
    return filteredItems.sort((a, b) => {
      if (a.categoryOrder !== b.categoryOrder) {
        return a.categoryOrder - b.categoryOrder;
      }
      return (a.order || 0) - (b.order || 0);
    });
  }, [template, transaction, documents, savedComplianceItems, showConditional, showOptional, brokerageReplacementMap]);

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const grouped = {};
    complianceItems.forEach((item) => {
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });
    return grouped;
  }, [complianceItems]);

  async function handleRequirementChange(item, newRequirement) {
    // Always use category-name key for consistency with savedComplianceItems lookup
    const itemKey = `${item.category}-${item.name}`;
    setProcessingItems((prev) => ({ ...prev, [itemKey]: true }));

    try {
      // If changing to "not-needed", update status
      // Otherwise, update the requirement level
      if (newRequirement === "not-needed") {
        await updateComplianceItemStatusInDb(transaction.id, itemKey, "not-needed");
        toast.success("Item marked as not needed");
      } else {
        // Update the requirement field
        await updateComplianceItemStatusInDb(transaction.id, itemKey, "pending", newRequirement);
        toast.success(`Requirement updated to ${newRequirement}`);
      }
    } catch (error) {
      console.error("Error updating compliance item:", error);
      toast.error("Failed to update item");
    } finally {
      setProcessingItems((prev) => ({ ...prev, [itemKey]: false }));
    }
  }

  function handleViewDocument(doc) {
    dispatch(
      openModal({
        modalType: "ComplianceDocumentPreviewModal",
        modalProps: { document: doc, transaction },
      })
    );
  }

  function getRowStyle(item) {
    if (item.status === "complete" || (item?.documents && item.documents.length > 0)) {
      return { backgroundColor: "#d4edda" }; // light green
    }
    if (item.status === "not-needed") {
      return { backgroundColor: "#5d6164ff" }; // light gray
    }
    return {};
  }

  function getRequirementBadge(item, isComplete, isProcessing) {
    const requirement = item.status === "not-needed" ? "not-needed" : item.effectiveRequirement || item.requirement;
// console.log("item status: ", item.status, " requirement: ", requirement);
    // If item is complete and required, show green instead of red
    const colors = {
      required: isComplete ? "green" : "red",
      optional: "grey",
      conditional: isComplete ? "green" : "orange",
      "not-needed": "grey",
    };

    const requirementOptions = [
      { key: "required", text: "Required", value: "required" },
      { key: "optional", text: "Optional", value: "optional" },
      { key: "conditional", text: "Conditional", value: "conditional" },
      { key: "not-needed", text: "Not Needed", value: "not-needed" },
    ];

    return (
      <>
        <Dropdown
          trigger={
            <Label
              size="tiny"
              color={colors[requirement] || "grey"}
              style={{ cursor: "pointer" }}
            >
              {requirement || "optional"}
              {/* <Icon name="dropdown" style={{ marginLeft: "4px" }} /> */}
            </Label>
          }
          options={requirementOptions}
          value={requirement}
          onChange={(_, { value }) => handleRequirementChange(item, value)}
          disabled={isProcessing || (item.documents && item.documents.length > 0)}
          selectOnBlur={false}
        />
        {(item.conditionalRequirementMessage || item.conditionalConditionText) && (
          <Popup
            size="small"
            trigger={
              <Icon
                name="info circle"
                color="blue"
                style={{ marginLeft: "6px", cursor: "pointer" }}
              />
            }
            content={item.conditionalRequirementMessage || `Condition: ${item.conditionalConditionText}`}
            position="top center"
          />
        )}
      </>
    );
  }

  function renderItemName(item) {
    return (
      <div>
        <strong>{item.name}</strong>
        {item.description && (
          <Popup
            size="small"
            trigger={
              <Icon
                name="info circle"
                color="blue"
                style={{ marginLeft: "6px", cursor: "pointer" }}
              />
            }
            content={item.description}
            position="top center"
          />
        )}
        {item.conditionalConditionText && (
          <div style={{ color: "#8a6d3b", fontSize: "0.85em", marginTop: "4px" }}>
            <Icon name="random" /> Condition: {item.conditionalConditionText}
          </div>
        )}
      </div>
    );
  }

  function formatUpdatedDate(date) {
    if (!date) return "—";

    // Handle Firestore timestamp
    if (date.seconds) {
      return format(new Date(date.seconds * 1000), "MM/dd/yyyy");
    }
    // Handle regular Date object
    if (date instanceof Date) {
      return format(date, "MM/dd/yyyy");
    }
    return "—";
  }

  // Filter uploaded brokerage form documents
  const uploadedBrokerageFormDocs = useMemo(() => {
    if (!documents || documents.length === 0) {
      return [];
    }
    return documents.filter((doc) => doc.isBrokerageForm === true);
  }, [documents]);

  // Build combined brokerage forms list with their matched documents
  // Exclude forms that replace parent forms (they show in the main compliance table)
  const brokerageFormItems = useMemo(() => {
    const agentRepresents = transaction?.agentRepresents || "Buyer";
    const matchedDocIds = new Set();

    // First, process forms from brokerageForms template
    const formItems = (brokerageForms || [])
      .map((form) => {
        // Find ALL matching uploaded documents by form title or formId
        const matchedDocs = uploadedBrokerageFormDocs.filter(
          (doc) => doc.title === form.title || doc.formId === form.id
        );

        // Track which documents have been matched
        matchedDocs.forEach((doc) => matchedDocIds.add(doc.id));

        return {
          ...form,
          documents: matchedDocs,
          isComplete: matchedDocs.length > 0,
          requirement: form.complianceRequirement || "optional",
        };
      })
      .filter((form) => {
        // Exclude forms that replace parent forms - they appear in the main compliance table
        if (form.brokerageReplacesParentFormTitle) {
          return false;
        }

        // Filter based on agent representation (showBuyerAgent/showSellerAgent)
        if (agentRepresents === "Buyer" || agentRepresents === "Tenant") {
          if (form.showBuyerAgent === false) return false;
        } else if (agentRepresents === "Seller" || agentRepresents === "Landlord") {
          if (form.showSellerAgent === false) return false;
        }

        // Always show forms that have been signed (have matching documents)
        if (form.isComplete) return true;

        const requirement = form.requirement;

        // Always show required items
        if (requirement === "required") return true;

        // Show optional only if checkbox is checked
        if (requirement === "optional" && showOptional) return true;

        // Show conditional only if checkbox is checked
        if (requirement === "conditional" && showConditional) return true;

        return false;
      });

    // Find orphaned brokerage form documents (signed but don't match any template)
    const orphanedDocs = uploadedBrokerageFormDocs.filter(
      (doc) => !matchedDocIds.has(doc.id)
    );

    // Add orphaned documents as their own items (always show since they're signed)
    const orphanedItems = orphanedDocs.map((doc) => ({
      id: doc.id,
      title: doc.title || doc.name || "Brokerage Form",
      documents: [doc],
      isComplete: true,
      requirement: "optional",
      isOrphanedDocument: true,
    }));

    return [...formItems, ...orphanedItems].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [brokerageForms, uploadedBrokerageFormDocs, transaction, showOptional, showConditional]);

  // Get all document names from template (for filtering "Other Forms")
  const templateDocumentNames = useMemo(() => {
    if (!template?.categories) return new Set();
    const names = new Set();
    template.categories.forEach((category) => {
      (category.items || []).forEach((item) => {
        if (item.documentName) {
          names.add(item.documentName);
        }
      });
    });
    return names;
  }, [template]);

  // Filter "Other Forms" - signing complete documents that are not brokerage forms
  // and did not match into a compliance item
  const otherFormDocuments = useMemo(() => {
    if (!documents || documents.length === 0) {
      return [];
    }
    return documents.filter((doc) => {
      // Must not be a brokerage form
      if (doc.isBrokerageForm === true) return false;
      // Must not match any compliance template item by documentName
      if (doc.documentName && templateDocumentNames.has(doc.documentName)) return false;
      return true;
    });
  }, [documents, templateDocumentNames]);

  function handleAssignDocumentType(doc) {
    dispatch(
      openModal({
        modalType: "ComplianceAssignDocumentTypeModal",
        modalProps: { document: doc, template },
      })
    );
  }

  return (
    <div>
      {Object.entries(itemsByCategory).map(([categoryName, items]) => (
        <div key={categoryName} className="medium bottom margin">
          <h3 className="small bottom margin">{categoryName}</h3>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>Type</Table.HeaderCell>
                <Table.HeaderCell width={5}>Document</Table.HeaderCell>
                <Table.HeaderCell width={2}>Requirement</Table.HeaderCell>
                <Table.HeaderCell width={5}>
                  File{" "}
                  <Popup
                    size="small"
                    trigger={
                      <Icon
                        name="info circle"
                        color="blue"
                        style={{
                          marginLeft: "6px",
                          cursor: "pointer",
                        }}
                      />
                    }
                    content={
                      <p>
                        Only documents that have a status of "Signing Complete" will appear in the compliance checklist.
                        <br />
                        Click on the document name to view the document.
                      </p>
                    }
                    position="top center"
                  />
                </Table.HeaderCell>
                <Table.HeaderCell width={2}>Updated</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item, index) => {
                const itemKey = `${item.category}-${item.name}-${index}`;
                const isProcessing = processingItems[itemKey];

                const isComplete =
                  item.status === "complete" || (item.documents && item.documents.length > 0);

                const hasDocuments = item.documents && item.documents.length > 0;

                // if (hasDocuments) {
                //   console.log(`[RENDER] Rendering ${item.documents.length} rows for "${item.name}"`);
                // }

                // If there are multiple documents, we need to render multiple rows
                if (hasDocuments) {
                  // console.log("ROW item.documents: ", item.documents);
                  const greenCellStyle = { backgroundColor: "#d4edda" };
                  return item.documents.map((doc, docIndex) => (
                    <Table.Row key={`${itemKey}-doc-${docIndex}`}>
                      <Table.Cell style={greenCellStyle}>
                        {docIndex === 0 && item.documentType ? (
                          <i>{item.documentType}</i>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        {docIndex === 0 ? (
	                          renderItemName(item)
                        ) : (
                          <div style={{ paddingLeft: "20px", color: "#999" }}>↳</div>
                        )}
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        {docIndex === 0 && getRequirementBadge(item, isComplete, isProcessing)}
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        <Button
                          size="tiny"
                          basic
                          onClick={() => handleViewDocument(doc)}
                        >
                          <Icon name="file pdf outline" />
                          {doc.name}
                        </Button>
                        <Button
                          size="tiny"
                          basic
                          secondary
                          style={{ marginLeft: "8px" }}
                          onClick={() => handleAssignDocumentType(doc)}
                        >
                          {doc.documentName ? "Change Type" : "Assign Type"}
                        </Button>
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        {doc.updatedAt
                          ? formatUpdatedDate(doc.updatedAt)
                          : doc.createdAt
                          ? formatUpdatedDate(doc.createdAt)
                          : "—"}
                      </Table.Cell>
                    </Table.Row>
                  ));
                }

                // Single row for items without documents
                return (
                  <Table.Row key={itemKey} style={getRowStyle(item)}>
                    <Table.Cell>
                      {item.documentType ? (
                        <i>{item.documentType}</i>
                      ) : null}
                    </Table.Cell>
                    <Table.Cell>
	                      {renderItemName(item)}
                    </Table.Cell>
                    <Table.Cell>
                      {getRequirementBadge(item, isComplete, isProcessing)}
                    </Table.Cell>
                    <Table.Cell>
                      {item.status === "not-needed" ? (
                        <span className="text-grey">Not needed</span>
                      ) : (
                        <span className="text-grey">—</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>—</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      ))}

      {/* Brokerage Forms Section - only when using the state default template */}
      {brokerageFormItems.length > 0 && template?.isDefault && (
        <div className="medium bottom margin">
          <h3 className="small bottom margin">Brokerage Forms</h3>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={5}>Document</Table.HeaderCell>
                <Table.HeaderCell width={2}>Requirement</Table.HeaderCell>
                <Table.HeaderCell width={5}>File</Table.HeaderCell>
                <Table.HeaderCell width={2}>Updated</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {brokerageFormItems.map((item, index) => {
                const requirementColors = {
                  required: item.isComplete ? "green" : "red",
                  optional: "grey",
                  conditional: item.isComplete ? "green" : "orange",
                };
                const greenCellStyle = { backgroundColor: "#d4edda" };

                // If there are documents, render multiple rows (one per document)
                if (item.documents && item.documents.length > 0) {
                  return item.documents.map((doc, docIndex) => (
                    <Table.Row key={`${item.id || index}-doc-${docIndex}`}>
                      <Table.Cell style={greenCellStyle}>
                        {docIndex === 0 ? (
                          <strong>{item.title}</strong>
                        ) : (
                          <div style={{ paddingLeft: "20px", color: "#999" }}>↳</div>
                        )}
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        {docIndex === 0 && (
                          <Label size="tiny" color={requirementColors[item.requirement] || "grey"}>
                            {item.requirement}
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        <Button
                          size="tiny"
                          basic
                          onClick={() => handleViewDocument(doc)}
                        >
                          <Icon name="file pdf outline" />
                          {doc.name}
                        </Button>
                      </Table.Cell>
                      <Table.Cell style={greenCellStyle}>
                        {doc.updatedAt
                          ? formatUpdatedDate(doc.updatedAt)
                          : doc.createdAt
                          ? formatUpdatedDate(doc.createdAt)
                          : "—"}
                      </Table.Cell>
                    </Table.Row>
                  ));
                }

                // Single row for items without documents (pending)
                return (
                  <Table.Row key={item.id || index}>
                    <Table.Cell>
                      <strong>{item.title}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="tiny" color={requirementColors[item.requirement] || "grey"}>
                        {item.requirement}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-grey">-</span>
                    </Table.Cell>
                    <Table.Cell>—</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Other Forms Section */}
      {otherFormDocuments.length > 0 && (
        <div className="medium bottom margin">
          <h3 className="small bottom margin">Other Forms</h3>
          <p className="small bottom margin text-grey">
            <i>These documents did not auto-assign to a compliance category. 
              Click on "Assign Type" to categorize each document into the appropriate compliance category, if desired.
            </i>
          </p>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={5}>Document</Table.HeaderCell>
                {/* <Table.HeaderCell width={2}>Requirement</Table.HeaderCell> */}
                <Table.HeaderCell width={5}>File</Table.HeaderCell>
                <Table.HeaderCell width={2}>Updated</Table.HeaderCell>
                <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {otherFormDocuments.map((doc, index) => (
                <Table.Row key={doc.id || index} style={ {backgroundColor: "#d4edda" }}>
                  <Table.Cell>
                    <strong>{doc.name}</strong>
                  </Table.Cell>
                  {/* <Table.Cell>
                    <Label size="tiny" color="grey">
                      optional
                    </Label>
                  </Table.Cell> */}
                  <Table.Cell>
                    <Button
                      size="tiny"
                      basic
                      onClick={() => handleViewDocument(doc)}
                    >
                      <Icon name="file pdf outline" />
                      {doc.name}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    {doc.updatedAt
                      ? formatUpdatedDate(doc.updatedAt)
                      : doc.createdAt
                      ? formatUpdatedDate(doc.createdAt)
                      : "—"}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="tiny"
                      primary
                      onClick={() => handleAssignDocumentType(doc)}
                    >
                      <Icon name="tag" />
                      Assign Type
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  );
}

